import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
  usePropertyField,
} from '@vibesites/runtime/react';
import {
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faYoutube } from '@fortawesome/free-brands-svg-icons';
import {
  formatDisplayPhone,
  condenseOfficeHours,
  type OfficeHourEntry,
} from '@vibesites/theme-oasis/components/PropertyInfoPanel';
// Black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so the base oasis fork keeps a dark-fill copy
// alongside it for use on light primary backgrounds. We re-import it
// from the base theme so the Weidner fork doesn't have to ship its own
// copy of the SVG. Same applies to the gold geometric diamond pattern
// shown as a horizontal band above the footer.
import repliBlackLogoUrl from '@vibesites/theme-oasis/assets/repli_black.svg';
import footerPatternUrl from '@vibesites/theme-oasis/assets/footer-pattern.png';

// CORT furnished-apartments lead form — same loader the `cort-lead-form`
// injection block uses. Preferred's Footer CTA only calls
// `window.Cort_modal.open()` and assumes an editor-added injection block
// already loaded the script; here we self-inject from
// `custom_cort_lead_form_id` so the Home footer works without that block.
// `loadCortForm` is async (fetch + setupCortForm) — never open the modal
// until that promise settles, or Cort_modal is still undefined.
const CORT_SCRIPT_ID = 'CortFormLoader';
const CORT_FORM_DIV_ID = 'CortFormDiv';
const CORT_SCRIPT_URL = 'https://api-assets.cort.com/scripts/CortFormLoader.js';

type CortWindow = Window & {
  Cort_modal?: { open?: () => void };
  loadCortForm?: (formId: string, mode: string) => void | Promise<void>;
};

function ensureCortFormHost(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(CORT_FORM_DIV_ID)) return;
  const formDiv = document.createElement('div');
  formDiv.id = CORT_FORM_DIV_ID;
  formDiv.hidden = true;
  formDiv.setAttribute('aria-hidden', 'true');
  document.body.appendChild(formDiv);
}

function loadCortFormScript(): Promise<void> {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return Promise.resolve();
  }
  const win = window as CortWindow;
  if (typeof win.loadCortForm === 'function') return Promise.resolve();

  const existing = document.getElementById(CORT_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      if (typeof (window as CortWindow).loadCortForm === 'function') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('CortFormLoader failed')), {
        once: true,
      });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = CORT_SCRIPT_ID;
    script.src = CORT_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('CortFormLoader failed'));
    document.body.appendChild(script);
  });
}

async function initCortLeadForm(formId: string): Promise<boolean> {
  if (!formId || typeof window === 'undefined') return false;
  ensureCortFormHost();
  try {
    await loadCortFormScript();
  } catch {
    return false;
  }
  const win = window as CortWindow;
  if (typeof win.loadCortForm !== 'function') return false;
  try {
    await win.loadCortForm(formId, 'Custom');
  } catch {
    return false;
  }
  return typeof win.Cort_modal?.open === 'function';
}

// ============================================================================
// Weidner Oasis fork of the base oasis Footer
// ----------------------------------------------------------------------------
// Forked from `@vibesites/theme-oasis/components/Footer.tsx` to mirror the
// canonical Weidner via6 reference
// (Repli-Multihub/vibesites-weidner-apartment-homes-via6). Structural
// deltas vs the base, all targeted at matching the via6 footer:
//
//   1. Text Us! link — base already ships the `footer.text_us_link` slot
//      group but defaults the URL to empty so the link is hidden until
//      an editor fills it in. The via6 reference exposes this affordance
//      prominently in the contact column, so we keep the same slot path
//      (preserves any editor-saved URL) and surface it visually with a
//      gold underline + bold weight — matching the via6 treatment — so
//      it reads as a primary contact action rather than a tertiary link.
//
//   2. Weidner Cares — new pill-style link in the Office Hours column
//      under the existing CORT CTA. Points at the Weidner-wide community
//      philanthropy program by default
//      (https://www.weidner.com/weidner-cares); editors can override the
//      URL/label per property.
//
//   3. Love Notes — new pill-style link rendered alongside Weidner Cares.
//      Points at the Weidner-wide resident testimonials hub by default
//      (https://www.weidner.com/love-notes); editors can override per
//      property.
//
// All other slot paths and tokens are preserved verbatim from the base
// so previously-saved content keeps mapping cleanly.
// ============================================================================

// ============================================================================
// Contrast text color
// ----------------------------------------------------------------------------
// Mirrors `getContrastTextColor` from `@vibesites/tokens` (defined in
// `vendor/vibesites/tokens/generator.ts`, currently module-private and not
// re-exported, so we replicate the exact logic here verbatim — including the
// `0.55` luminance threshold and the `'#ffffff'` / `'#1a1a1a'` return values).
// Using the same function keeps logo color choice (white vs black) in lockstep
// with the auto-computed `--vibe-color-text-on-primary` CSS variable, so the
// footer's text and logos always pick the same contrast side.
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
const TEXT_ON_PRIMARY_DARK = '#1a1a1a';

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function getContrastTextColor(bgHex: string | null | undefined): string {
  if (!bgHex) return TEXT_ON_PRIMARY_LIGHT;
  const luminance = getRelativeLuminance(bgHex);
  return luminance > 0.55 ? TEXT_ON_PRIMARY_DARK : TEXT_ON_PRIMARY_LIGHT;
}

// ============================================================================
// Hours label formatters (footer-only)
// ============================================================================

function titleCaseDayLabel(str: string): string {
  return str.replace(/[A-Za-z]+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
}

function lowercaseMeridiem(str: string): string {
  return str.replace(/\b(AM|PM)\b/g, (s) => s.toLowerCase());
}

// ── Office hours ordering ────────────────────────────────────────────────
// The bound `office_hours` collection arrives in whatever order the source
// (e.g. Yardi) emits — often not Monday-first (the sample data comes through
// as TUE, WED, THU, FRI, MON). `condenseOfficeHours` collapses *consecutive*
// equal-time rows and otherwise preserves input order, so we sort to a
// canonical Monday→Sunday week before condensing. Unknown/blank day codes
// sort to the end (and keep their relative order) so they never break the
// Mon–Fri grouping.
const DAY_ORDER: Record<string, number> = {
  MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6,
};

function dayRank(day: string | null | undefined): number {
  const key = (day ?? '').trim().slice(0, 3).toUpperCase();
  return key in DAY_ORDER ? DAY_ORDER[key] : Number.MAX_SAFE_INTEGER;
}

function sortOfficeHoursMondayFirst(hours: OfficeHourEntry[]): OfficeHourEntry[] {
  return hours
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const rank = dayRank(a.entry.day) - dayRank(b.entry.day);
      // Stable fallback for entries that share a rank (e.g. unknown days).
      return rank !== 0 ? rank : a.index - b.index;
    })
    .map(({ entry }) => entry);
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const brandLogoDef: SlotGroupDefinition = {
  label: 'Brand Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: [
        'property_mono_white_logo',
        'property_mono_black_logo',
        'property_primary_logo',
        'property_secondary_logo',
      ],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Logo',
    },
    url: { label: 'URL', type: 'url', defaultValue: '/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeLinkDef: SlotGroupDefinition = {
  label: 'YouTube Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_youtube_url',
      suggestedFields: ['property_youtube_url'],
      defaultValue: 'https://www.youtube.com/user/WeidnerApartments',
    },
    label: { label: 'Label', type: 'text', defaultValue: 'YouTube' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ── Text Us link (between address and phone) ─────────────────────────────
// No `defaultField` — properties don't have a canonical SMS-widget field,
// so editors paste the URL from their texting provider. Hidden in the
// rendered footer when no URL is set. The Weidner via6 reference styles
// this link as a primary action (gold underline + bold) rather than the
// muted tertiary look the base oasis ships with — the visual treatment
// is applied in CSS below via the `footer-textus-link` class.
const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: { label: 'Display Text', type: 'text', defaultValue: 'Text Us!' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const ctaButtonDef: SlotGroupDefinition = {
  label: 'Footer CTA Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '' },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ── Weidner Cares link (Weidner-wide philanthropy program) ───────────────
// Rendered as a clickable badge — the canonical via6 footer
// (site-via-six.pages.dev) shows this as an image icon hosted on the
// Multihub CDN rather than a text pill, so the slot group is centered
// on the `image` field. The `alt` field doubles as the accessible
// label / tooltip; editors can repoint at a property-specific page if
// desired. Hidden in the rendered footer when both the image and URL
// are cleared.
const weidnerCaresLinkDef: SlotGroupDefinition = {
  label: 'Weidner Cares Link',
  fields: {
    image: {
      label: 'Icon Image',
      type: 'image',
      defaultValue:
        'https://multihubmedia.com/cdn-cgi/image/format=auto,w=640/company/5726763346821120/019d44f3-3452-767e-8966-83b6939f057f.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Weidner Cares' },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.weidner.com/weidnercares',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ── Love Notes link (resident testimonials hub) ──────────────────────────
// Same image-badge pattern as Weidner Cares above. URL note: the
// canonical Weidner path is `/lovenotes` (one word, no hyphen), matching
// what the via6 reference links to.
const loveNotesLinkDef: SlotGroupDefinition = {
  label: 'Love Notes Link',
  fields: {
    image: {
      label: 'Icon Image',
      type: 'image',
      defaultValue:
        'https://multihubmedia.com/cdn-cgi/image/format=auto,w=640/company/5726763346821120/019d44f3-3412-75a9-9aac-8f5678cb59df.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Love Notes' },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.weidner.com/lovenotes',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo', 'property_company_mono_black_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const runtimeBrandLogoDef: SlotGroupDefinition = useWhiteOnPrimary
    ? brandLogoDef
    : {
        ...brandLogoDef,
        fields: {
          ...brandLogoDef.fields,
          image: {
            ...brandLogoDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  const runtimeManagedByLogoDef: SlotGroupDefinition = useWhiteOnPrimary
    ? managedByLogoDef
    : {
        ...managedByLogoDef,
        fields: {
          ...managedByLogoDef.fields,
          image: {
            ...managedByLogoDef.fields.image,
            defaultField: 'property_company_mono_black_logo',
          },
        },
      };

  // ── Top contact block (logo / contact / hours) ──────────────────────────
  const brandLogo = useSlotGroup('footer.brand_logo', runtimeBrandLogoDef);
  const facebook = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const youtube = useSlotGroup('footer.youtube_link', youtubeLinkDef);
  const textUs = useSlotGroup('footer.text_us_link', textUsLinkDef);
  const ctaButton = useSlotGroup('footer.cta_button', ctaButtonDef);
  // Weidner-specific community links — see slot group comments above.
  const weidnerCares = useSlotGroup('footer.weidner_cares_link', weidnerCaresLinkDef);
  const loveNotes = useSlotGroup('footer.love_notes_link', loveNotesLinkDef);

  const address = useContentToken('footer.address');
  const phone = useContentToken('footer.phone') || '';
  const phoneDisplay = phone ? formatDisplayPhone(phone) : '';
  // Resolve the address link from the same source the header uses: the
  // property's Google Maps place page (`property_google_place_url`), which is
  // the `defaultField` of the header's `header.address_link` slot group —
  // rather than a generic maps search query built from the address text.
  const addressUrl = usePropertyField('property_google_place_url') || '';

  const officeHours = useCollectionBinding<OfficeHourEntry>('footer.office_hours', {
    collection: 'office_hours',
  });
  const hasHours = officeHours.length > 0;
  const hoursRows = hasHours
    ? condenseOfficeHours(sortOfficeHoursMondayFirst(officeHours))
    : [];

  // CORT lead-form CTA — same behavior as preferred Footer › cort_button_text:
  // visible only when the property has a CORT form ID; click opens
  // `window.Cort_modal.open()`. Prefetch the async loader on mount so the
  // first click usually opens immediately.
  const cortButtonText = useContentToken('footer.cort_button_text');
  const cortLeadFormId = (
    (usePropertyField('custom_cort_lead_form_id') as string) || ''
  ).trim();
  const cortReadyRef = React.useRef(false);
  const cortInitPromiseRef = React.useRef<Promise<boolean> | null>(null);

  const ensureCortReady = React.useCallback(() => {
    if (!cortLeadFormId) return Promise.resolve(false);
    if (cortReadyRef.current && typeof (window as CortWindow).Cort_modal?.open === 'function') {
      return Promise.resolve(true);
    }
    if (!cortInitPromiseRef.current) {
      cortInitPromiseRef.current = initCortLeadForm(cortLeadFormId).then((ok) => {
        cortReadyRef.current = ok;
        if (!ok) cortInitPromiseRef.current = null;
        return ok;
      });
    }
    return cortInitPromiseRef.current;
  }, [cortLeadFormId]);

  React.useEffect(() => {
    if (!cortLeadFormId) return;
    void ensureCortReady();
  }, [cortLeadFormId, ensureCortReady]);

  const openCortModal = React.useCallback(() => {
    if (!cortLeadFormId || typeof window === 'undefined') return;
    const cort = (window as CortWindow).Cort_modal;
    if (cort?.open) {
      cort.open();
      return;
    }
    // Loader still fetching / setting up — open once Cort_modal exists.
    void ensureCortReady().then((ok) => {
      if (ok) (window as CortWindow).Cort_modal?.open?.();
    });
  }, [cortLeadFormId, ensureCortReady]);

  const hasAnySocial = Boolean(facebook.url || instagram.url || youtube.url);

  // ── Attribution row ─────────────────────────────────────────────────────
  const managedByLogo = useSlotGroup('footer.managed_by_logo', runtimeManagedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe IQ widget injects into <body> and sets its own inline
  // `style="z-index: …"` after mount, which beats a stylesheet unless we
  // override with `!important`. Footer mounts on every page via DefaultLayout,
  // so this is a single global mount point without a new always-on component.
  //
  // Keep the pin tight: only the real launcher nodes (`.rcc-app-cta-wrap`,
  // `.rcc-app-controls`, `#conversations-webchat-toggle`), at 120 — just above
  // the open header / offer-banner stack (~100). Do not walk up to pin an
  // outermost fixed ancestor unless that ancestor is the chat wrap itself
  // (covered by matching `.rcc-app-cta-wrap` directly).
  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    const SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const Z = '120';

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!el.matches(SELECTOR)) return;
      pin(el);
    }

    function scanSubtree(root: ParentNode) {
      for (const c of Array.from(root.querySelectorAll(SELECTOR))) {
        processCandidate(c);
      }
      if (root instanceof Element) processCandidate(root);
    }

    scanSubtree(document.body);

    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div
        className="footer-pattern-row"
        aria-hidden="true"
        role="presentation"
      />

      <footer
        className="text-primary-foreground footer-root"
        style={{ backgroundColor: 'var(--vibe-color-primary)' }}
      >
        <div className="footer-top max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="footer-top-grid">

            {/* ---- Col 1: Brand ---- */}
            <div className="footer-brand-col">
              <EditableSlotGroup groupPath="footer.brand_logo" definition={runtimeBrandLogoDef}>
                {brandLogo.image ? (
                  <a
                    href={brandLogo.url || '/'}
                    target={brandLogo._target || '_self'}
                    className="footer-brand-link"
                    aria-label={brandLogo.alt || 'Home'}
                  >
                    <VibesitesImage
                      url={brandLogo.image}
                      alt={brandLogo.alt || 'Property Logo'}
                      className="footer-brand-logo"
                    />
                  </a>
                ) : (
                  <div className="footer-brand-fallback" aria-hidden="true" />
                )}
              </EditableSlotGroup>

              <p className="footer-copyright">
                © {new Date().getFullYear()} All Rights Reserved.
              </p>

              {hasAnySocial && (
                <div className="footer-social-row" role="list" aria-label="Social links">
                  {facebook.url && (
                    <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                      <a
                        href={facebook.url}
                        target={facebook._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={facebook.label || 'Facebook'}
                        className="footer-social-link"
                        role="listitem"
                      >
                        <FontAwesomeIcon icon={faFacebook} className="footer-social-icon" />
                      </a>
                    </EditableSlotGroup>
                  )}
                  {instagram.url && (
                    <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                      <a
                        href={instagram.url}
                        target={instagram._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={instagram.label || 'Instagram'}
                        className="footer-social-link"
                        role="listitem"
                      >
                        <FontAwesomeIcon icon={faInstagram} className="footer-social-icon" />
                      </a>
                    </EditableSlotGroup>
                  )}
                  {youtube.url && (
                    <EditableSlotGroup groupPath="footer.youtube_link" definition={youtubeLinkDef}>
                      <a
                        href={youtube.url}
                        target={youtube._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={youtube.label || 'YouTube'}
                        className="footer-social-link"
                        role="listitem"
                      >
                        <FontAwesomeIcon icon={faYoutube} className="footer-social-icon" />
                      </a>
                    </EditableSlotGroup>
                  )}
                </div>
              )}
            </div>

            {/* ---- Col 2: Contact Us ---- */}
            <div className="footer-col">
              <RichTextContent
                path="footer.contact_heading"
                defaultValue="<p>Contact Us</p>"
                as="h3"
                className="footer-col-heading"
              />

              <div className="footer-contact-list">
                {address ? (
                  addressUrl ? (
                    <a
                      href={addressUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-contact-link"
                    >
                      <EditableToken path="footer.address" as="span">
                        {address}
                      </EditableToken>
                    </a>
                  ) : (
                    <EditableToken path="footer.address" as="p" className="footer-contact-line">
                      {address}
                    </EditableToken>
                  )
                ) : null}

                {/* Text Us! — gold underline + bold on the Weidner fork so
                    the link reads as a primary contact action alongside the
                    address and phone, matching the via6 footer treatment. */}
                {textUs.url && (
                  <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                    <a
                      href={textUs.url}
                      target={textUs._target || '_blank'}
                      rel="noopener noreferrer"
                      className="footer-contact-link footer-textus-link"
                    >
                      {textUs.label || 'Text Us!'}
                    </a>
                  </EditableSlotGroup>
                )}

                {phone && (
                  <a
                    href={`tel:${phone}`}
                    className="dniphone dniphonehref footer-contact-link"
                  >
                    <EditableToken path="footer.phone" as="span">
                      {phoneDisplay}
                    </EditableToken>
                  </a>
                )}
              </div>
            </div>

            {/* ---- Col 3: Office Hours + CTA + Community Links ---- */}
            <div className="footer-col">
              <RichTextContent
                path="footer.hours_heading"
                defaultValue="<p>Office Hours</p>"
                as="h3"
                className="footer-col-heading"
              />

              {hasHours && (
                <div className="footer-hours-grid">
                  {hoursRows.map((row, i) => (
                    <div key={i} className="footer-hours-row">
                      <span className="footer-hours-day">{titleCaseDayLabel(row.dayLabel)}</span>
                      <span className="footer-hours-time">{lowercaseMeridiem(row.timeLabel)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* CORT lead form CTA — hidden unless the property has a CORT form ID.
                  Styled to match `.footer-cta-button` (secondary fill, square). */}
              {cortLeadFormId ? (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={openCortModal}
                    className="footer-cort-btn group/cort"
                  >
                    <EditableToken path="footer.cort_button_text" as="span" className="footer-cort-btn-label">
                      {cortButtonText || 'Furnish Your Home with CORT'}
                    </EditableToken>
                    <svg className="footer-cort-btn-icon shrink-0 transition-transform duration-200 group-hover/cort:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </button>
                </div>
              ) : null}

              {ctaButton.url && (
                <EditableSlotGroup groupPath="footer.cta_button" definition={ctaButtonDef}>
                  <a
                    href={ctaButton.url}
                    target={ctaButton._target || '_blank'}
                    rel="noopener noreferrer"
                    className="footer-cta-button"
                  >
                    {ctaButton.label || 'Learn More'}
                  </a>
                </EditableSlotGroup>
              )}

              {/* Weidner-specific community icons — render as a horizontal
                  pair of clickable badges under the CORT CTA. The via6
                  reference (site-via-six.pages.dev) ships these as
                  rectangular brand badges hosted on the Multihub CDN
                  (not text pills or font icons), so we render the slot's
                  `image` directly via VibesitesImage. Each badge is
                  independently toggleable — clearing both image+url on a
                  slot collapses just that badge so the row stays balanced
                  if only one link is configured for a given property.

                  Mono treatment: Multihub only hosts a single colored
                  source for each badge, so to render the icons as the
                  mono-black variant the via6 reference uses we apply a
                  CSS filter (`brightness(0)`) that flattens any colored
                  source to a solid silhouette. The silhouette color
                  flips with the primary background's contrast side —
                  solid black on light primaries, solid white on dark —
                  mirroring the brand-logo / managed-by-logo
                  contrast-flip logic above so the badges stay visible
                  regardless of how dark/light the property's primary
                  color is. The two CSS variant classes are driven by
                  `useWhiteOnPrimary` (computed at the top of this
                  component). */}
              {(weidnerCares.image || loveNotes.image) && (
                <div className="footer-community-row" aria-label="Weidner community links">
                  {weidnerCares.image && (
                    <EditableSlotGroup
                      groupPath="footer.weidner_cares_link"
                      definition={weidnerCaresLinkDef}
                    >
                      <a
                        href={weidnerCares.url || '#'}
                        target={weidnerCares._target || '_blank'}
                        rel="noopener noreferrer"
                        className="footer-community-icon-link"
                        aria-label={weidnerCares.alt || 'Weidner Cares'}
                      >
                        <VibesitesImage
                          url={weidnerCares.image}
                          alt={weidnerCares.alt || 'Weidner Cares'}
                          className={`footer-community-icon ${
                            useWhiteOnPrimary
                              ? 'footer-community-icon--mono-white'
                              : 'footer-community-icon--mono-black'
                          }`}
                        />
                      </a>
                    </EditableSlotGroup>
                  )}
                  {loveNotes.image && (
                    <EditableSlotGroup
                      groupPath="footer.love_notes_link"
                      definition={loveNotesLinkDef}
                    >
                      <a
                        href={loveNotes.url || '#'}
                        target={loveNotes._target || '_blank'}
                        rel="noopener noreferrer"
                        className="footer-community-icon-link"
                        aria-label={loveNotes.alt || 'Love Notes'}
                      >
                        <VibesitesImage
                          url={loveNotes.image}
                          alt={loveNotes.alt || 'Love Notes'}
                          className={`footer-community-icon ${
                            useWhiteOnPrimary
                              ? 'footer-community-icon--mono-white'
                              : 'footer-community-icon--mono-black'
                          }`}
                        />
                      </a>
                    </EditableSlotGroup>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>

        <div className="footer-divider" aria-hidden="true" />

        <div className="footer-compliance-row" aria-label="Compliance">
          <WheelchairIcon size={24} color="currentColor" />
          <FairHousingIcon size={24} color="currentColor" />
          <PetsAllowedIcon size={24} color="currentColor" />
        </div>

        <div className="footer-attribution max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="footer-attribution-grid">

            <div className="footer-attribution-col footer-attribution-left">
              {managedByLogo.image ? (
                <EditableSlotGroup groupPath="footer.managed_by_logo" definition={runtimeManagedByLogoDef}>
                  <div className="footer-attribution-item">
                    <RichTextContent
                      path="footer.managed_by_text"
                      defaultValue="<p>Professionally Managed by</p>"
                      as="span"
                      className="footer-attribution-label"
                    />
                    <a
                      href={managedByLogo.url || '#'}
                      target={managedByLogo._target || '_blank'}
                      rel="noopener noreferrer"
                      className="footer-attribution-logo-link"
                    >
                      <VibesitesImage
                        url={managedByLogo.image}
                        alt={managedByLogo.alt || 'Property Management Company'}
                        className="footer-attribution-logo"
                      />
                    </a>
                  </div>
                </EditableSlotGroup>
              ) : null}
            </div>

            <div className="footer-attribution-col footer-attribution-center">
              <nav className="footer-legal-nav" aria-label="Legal links">
                <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
                  <a
                    href={accessibilityLink.url || '/accessibility'}
                    target={accessibilityLink._target || '_self'}
                    className="footer-legal-link"
                  >
                    {accessibilityLink.text || 'Accessibility'}
                  </a>
                </EditableSlotGroup>
                <span aria-hidden="true" className="footer-legal-sep">·</span>
                <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
                  <a
                    href={privacyPolicyLink.url || '/privacy-policy'}
                    target={privacyPolicyLink._target || '_self'}
                    className="footer-legal-link"
                  >
                    {privacyPolicyLink.text || 'Privacy Policy'}
                  </a>
                </EditableSlotGroup>
                <span aria-hidden="true" className="footer-legal-sep">·</span>
                <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                  <a
                    href={sitemapLink.url || '/sitemap'}
                    target={sitemapLink._target || '_self'}
                    className="footer-legal-link"
                  >
                    {sitemapLink.text || 'Sitemap'}
                  </a>
                </EditableSlotGroup>
              </nav>

              <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
                <div className="footer-attribution-item">
                  <RichTextContent
                    path="footer.powered_by_text"
                    defaultValue="<p>Powered by</p>"
                    as="span"
                    className="footer-attribution-label"
                  />
                  <a
                    href={poweredByLogo.url || 'https://www.multihub.io/'}
                    target={poweredByLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    className="footer-attribution-logo-link"
                  >
                    {useWhiteOnPrimary ? (
                      <MultihubLogoMonoWhite height={22} alt={poweredByLogo.alt || 'Multihub'} />
                    ) : (
                      <MultihubLogoMonoBlack height={22} alt={poweredByLogo.alt || 'Multihub'} />
                    )}
                  </a>
                </div>
              </EditableSlotGroup>
            </div>

            <div className="footer-attribution-col footer-attribution-right">
              <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
                <div className="footer-attribution-item">
                  <RichTextContent
                    path="footer.design_by_text"
                    defaultValue="<p>Website Design by</p>"
                    as="span"
                    className="footer-attribution-label"
                  />
                  <a
                    href={designByLogo.url || 'https://www.repli360.com/'}
                    target={designByLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    className="footer-attribution-logo-link"
                  >
                    <VibesitesImage
                      url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                      alt={designByLogo.alt || 'Repli'}
                      height={28}
                      className="footer-attribution-repli"
                      priority
                    />
                  </a>
                </div>
              </EditableSlotGroup>
            </div>

          </div>

          <RichTextContent
            path="footer.verification_notice"
            defaultValue="<p>We participate in identity verification of all applicants.</p>"
            as="p"
            className="footer-verification"
          />
        </div>

      </footer>

      <style>{`
        /* ============================================================
           Footer pattern row
           ============================================================ */
        .footer-pattern-row {
          width: 100%;
          background-color: var(--vibe-color-background, #ffffff);
          background-image: url(${footerPatternUrl});
          background-repeat: repeat;
          background-size: 35%;
          background-position: 50% 0;
          padding: 22px 40px;
          line-height: 0;
        }
        @media (min-width: 768px) {
          .footer-pattern-row { padding: 28px 40px; }
        }
        @media (min-width: 1200px) {
          .footer-pattern-row { padding: 34px 40px; }
        }

        /* ============================================================
           TOP ROW
           ============================================================ */
        .footer-top {
          padding-top: 1.75rem;
          padding-bottom: 2rem;
          color: var(--vibe-color-text-on-primary, #ffffff);
        }
        @media (min-width: 768px) {
          .footer-top {
            padding-top: 2.25rem;
            padding-bottom: 2.5rem;
          }
        }
        .footer-top-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: start;
          text-align: center;
          justify-items: center;
        }
        @media (min-width: 768px) {
          .footer-top-grid {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
            gap: 2.5rem;
          }
        }
        @media (min-width: 1024px) {
          .footer-top-grid { gap: 3.5rem; }
        }

        .footer-brand-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }
        .footer-brand-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
        }
        .footer-brand-logo {
          display: block;
          width: auto;
          max-width: 220px;
          max-height: 110px;
          height: auto;
          object-fit: contain;
        }
        @media (min-width: 768px) {
          .footer-brand-logo {
            max-width: 240px;
            max-height: 130px;
          }
        }
        .footer-brand-fallback {
          width: 180px;
          height: 60px;
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 8%, transparent);
        }
        .footer-copyright {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.5;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.75;
          margin: 0;
        }

        .footer-social-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.75rem;
        }
        .footer-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.25rem;
          height: 2.25rem;
          border-radius: 9999px;
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 12%, transparent);
          color: var(--vibe-color-text-on-primary, #ffffff);
          text-decoration: none;
          transition: background-color 0.2s ease, transform 0.2s ease;
        }
        .footer-social-link:hover,
        .footer-social-link:focus-visible {
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 22%, transparent);
          transform: translateY(-1px);
        }
        .footer-social-link:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #ffffff);
          outline-offset: 2px;
        }
        .footer-social-icon {
          width: 1.05rem;
          height: 1.05rem;
        }

        .footer-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          width: 100%;
        }
        .footer-col-heading {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          line-height: 1.3;
          font-weight: 700;
          letter-spacing: 0;
          margin: 0 0 0.25rem 0;
          color: var(--vibe-color-text-on-primary, #ffffff);
        }
        .footer-col-heading p { margin: 0; }

        .footer-contact-list {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          width: 100%;
          align-items: center;
        }
        .footer-contact-line,
        .footer-contact-link {
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          line-height: 1.5;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.9;
          text-decoration: none;
          margin: 0;
          transition: opacity 0.2s ease;
          white-space: normal;
          text-align: center;
        }
        .footer-contact-link:hover,
        .footer-contact-link:focus-visible {
          opacity: 1;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        /* ── Weidner-specific Text Us! treatment ──
           Gold underline + bold weight so the link reads as a primary
           action alongside the address and phone, matching the via6
           footer treatment. The secondary color is the gold accent on
           Weidner properties; we fall back to the contrast text color
           when secondary is unset so the link still reads as a link. */
        .footer-textus-link {
          color: var(--vibe-color-secondary, var(--vibe-color-text-on-primary, #ffffff));
          opacity: 1;
          font-weight: 700;
          text-decoration: underline;
          text-underline-offset: 4px;
          text-decoration-thickness: 2px;
          align-self: center;
        }
        .footer-textus-link:hover,
        .footer-textus-link:focus-visible {
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 1;
        }

        .footer-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          width: 100%;
          max-width: 200px;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .footer-hours-grid { max-width: 220px; }
        }
        .footer-hours-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          line-height: 1.5;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.9;
        }
        .footer-hours-day { font-weight: 400; }
        .footer-hours-time { font-weight: 400; }

        .footer-cta-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-top: 0.75rem;
          padding: 0.75rem 1.25rem;
          background: var(--vibe-color-secondary, var(--vibe-color-primary));
          color: var(--vibe-color-text-on-secondary, var(--vibe-color-text-on-primary, #ffffff));
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 500;
          letter-spacing: 0;
          text-decoration: none;
          border: 0;
          transition: filter 0.2s ease, transform 0.2s ease;
          width: 100%;
          max-width: 280px;
        }
        @media (min-width: 768px) {
          .footer-cta-button { max-width: 300px; }
        }
        .footer-cta-button:hover,
        .footer-cta-button:focus-visible {
          filter: brightness(1.05);
          transform: translateY(-1px);
        }
        .footer-cta-button:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #ffffff);
          outline-offset: 2px;
        }

        /* Match .footer-cta-button: secondary fill, square corners. */
        .footer-cort-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 0.75rem;
          padding: 0.75rem 1.25rem;
          width: 100%;
          max-width: 280px;
          border: 0;
          border-radius: 0;
          background: var(--vibe-color-secondary, var(--vibe-color-primary));
          color: var(--vibe-color-text-on-secondary, var(--vibe-color-text-on-primary, #ffffff));
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 500;
          letter-spacing: 0;
          line-height: 1.25;
          cursor: pointer;
          transition: filter 0.2s ease, transform 0.2s ease;
        }
        @media (min-width: 768px) {
          .footer-cort-btn { max-width: 300px; }
        }
        .footer-cort-btn:hover,
        .footer-cort-btn:focus-visible {
          filter: brightness(1.05);
          transform: translateY(-1px);
        }
        .footer-cort-btn:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #ffffff);
          outline-offset: 2px;
        }
        .footer-cort-btn-label {
          text-align: center;
          text-wrap: balance;
          line-height: 1.3;
        }
        .footer-cort-btn-icon {
          width: 0.875rem;
          height: 0.875rem;
        }

        /* ── Weidner community badges (Weidner Cares + Love Notes) ──
           Horizontal pair of image badges under the CORT CTA, mirroring
           the via6 reference (site-via-six.pages.dev). Each badge is a
           clickable rectangle hosting a brand image — we cap the height
           rather than the width so the two badges visually balance even
           if the underlying source assets have different aspect ratios.
           Hover state lifts each badge slightly + brightens for parity
           with the other CTA affordances in the footer. */
        .footer-community-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 1.75rem;
          width: 100%;
          max-width: 320px;
          margin: 0.25rem auto 0;
        }
        @media (min-width: 768px) {
          .footer-community-row { gap: 2.25rem; }
        }
        .footer-community-icon-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          background: transparent;
          border: 0;
          text-decoration: none;
          opacity: 0.95;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .footer-community-icon-link:hover,
        .footer-community-icon-link:focus-visible {
          opacity: 1;
          transform: translateY(-1px);
        }
        .footer-community-icon-link:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #ffffff);
          outline-offset: 3px;
          border-radius: 4px;
        }
        .footer-community-icon {
          display: block;
          height: 34px;
          width: auto;
          max-width: 90px;
          object-fit: contain;
        }
        @media (min-width: 768px) {
          .footer-community-icon { height: 38px; max-width: 100px; }
        }
        /* ── Mono treatment ──
           Flatten the colored source asset to a single silhouette color
           so the badges read as the via6 reference's mono treatment.
           brightness(0) zeroes every channel → solid black (used on
           light primary backgrounds). On dark primary backgrounds we
           additionally invert(1) to flip the silhouette to white so
           the badges stay visible. The CSS filter chain runs after
           brightness(0) makes the entire image #000, so invert(1)
           predictably produces #fff regardless of the source palette. */
        .footer-community-icon--mono-black {
          filter: brightness(0) saturate(100%);
        }
        .footer-community-icon--mono-white {
          filter: brightness(0) saturate(100%) invert(1);
        }

        .footer-divider {
          max-width: 80rem;
          margin: 0 auto;
          height: 3px;
          background: var(--vibe-color-text-on-primary, #ffffff);
        }

        .footer-compliance-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          padding: 1.75rem 1rem;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.7;
        }
        @media (min-width: 768px) {
          .footer-compliance-row {
            gap: 2.5rem;
            padding: 2rem 1rem;
          }
        }

        .footer-attribution {
          padding-bottom: 1.75rem;
        }
        .footer-attribution-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          align-items: center;
          text-align: center;
          justify-items: center;
        }
        @media (min-width: 768px) {
          .footer-attribution-grid {
            grid-template-columns: 1fr 1fr 1fr;
            gap: 1.5rem;
            align-items: center;
          }
          .footer-attribution-left {
            justify-self: start;
            text-align: left;
          }
          .footer-attribution-center {
            justify-self: center;
            text-align: center;
          }
          .footer-attribution-right {
            justify-self: end;
            text-align: right;
          }
        }
        .footer-attribution-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }
        @media (min-width: 768px) {
          .footer-attribution-left { align-items: flex-start; }
          .footer-attribution-right { align-items: flex-end; }
        }
        .footer-attribution-item {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          gap: 0.4rem;
        }
        @media (min-width: 768px) {
          .footer-attribution-left .footer-attribution-item { align-items: flex-start; }
          .footer-attribution-right .footer-attribution-item { align-items: flex-end; }
        }
        .footer-attribution-label {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.4;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.7;
        }
        .footer-attribution-label p { margin: 0; }
        .footer-attribution-logo-link {
          display: inline-flex;
          align-items: center;
          opacity: 0.9;
          transition: opacity 0.2s ease;
        }
        .footer-attribution-logo-link:hover,
        .footer-attribution-logo-link:focus-visible {
          opacity: 1;
        }
        .footer-attribution-logo {
          height: 32px;
          width: auto;
          max-width: 180px;
          object-fit: contain;
          display: block;
        }
        .footer-attribution-repli {
          height: 28px;
          width: auto;
          display: block;
        }

        .footer-legal-nav {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.5;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.85;
        }
        .footer-legal-link {
          color: inherit;
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        .footer-legal-link:hover,
        .footer-legal-link:focus-visible {
          opacity: 0.7;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .footer-legal-sep {
          opacity: 0.5;
        }

        .footer-verification {
          margin: 1.75rem auto 0;
          max-width: 36rem;
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.6;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.7;
        }
        .footer-verification p { margin: 0; }

        /* RentCafe Connect / IQ launcher — first-pass stylesheet pin.
           Authoritative override lives in the MutationObserver above
           (setProperty with important priority beats the vendor's inline
           z-index). Only the real launcher nodes; z-index 120 sits just
           above the open header / offer-banner stack. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.contact_heading': {
      label: 'Contact Heading',
      type: 'richtext',
      defaultValue: '<p>Contact Us</p>',
    },
    'footer.hours_heading': {
      label: 'Hours Heading',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'footer.verification_notice': {
      label: 'Identity Verification Notice',
      type: 'richtext',
      defaultValue: '<p>We participate in identity verification of all applicants.</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
    'footer.cort_button_text': {
      label: 'CORT Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
  },
  slotGroups: {
    'footer.brand_logo': brandLogoDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.youtube_link': youtubeLinkDef,
    'footer.text_us_link': textUsLinkDef,
    'footer.cta_button': ctaButtonDef,
    // ── Weidner-specific community links ──────────────────────────────────
    'footer.weidner_cares_link': weidnerCaresLinkDef,
    'footer.love_notes_link': loveNotesLinkDef,
    // ── Attribution row ───────────────────────────────────────────────────
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
